import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rolesApi } from './api'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import type { ListParams, CreateRoleRequest, UpdateRoleRequest } from './api'

export function useRoles(params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ['roles', params],
        queryFn: () => rolesApi.getRoles(params),
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useRole(id: number) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ['roles', id],
        queryFn: () => rolesApi.getRole(id),
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

export function useCreateRole() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: CreateRoleRequest) => rolesApi.createRole(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] })
        },
    })
}

export function useUpdateRole() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateRoleRequest }) => {
            console.log('useUpdateRole mutation called:', { id, data })
            return rolesApi.updateRole(id, data)
        },
        onSuccess: (data) => {
            console.log('useUpdateRole success:', data)
            queryClient.invalidateQueries({ queryKey: ['roles'] })
        },
        onError: (error) => {
            console.error('useUpdateRole error:', error)
        }
    })
}

export function useDeleteRole() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => rolesApi.deleteRole(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] })
            toast({
                title: 'Role Deleted',
                description: 'Role has been deleted successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}
